/* AIRiskCheckPreview.jsx — Path 9 full-content detail page (compact.v2 dataset).
 *   • Dark slate result card: role title + department + four labelled points
 *     (headings read from sectionRegistry, text rendered verbatim)
 *   • Shared prediction notice shown once, above the card
 *   • Shared notes (overview, reading notes, model research, limitations,
 *     sources) in the existing Accordion primitive — once per pathway
 * Strict lock: backend /api/ai-risk-check/check returns 403 unless kept.
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Loader2, ChevronLeft, Zap, Lock, Info } from 'lucide-react';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Accordion } from '../components/preview-ui/PreviewPrimitives';
import { useAuth } from '../lib/auth';

const API = process.env.REACT_APP_BACKEND_URL;

const POINT_TONES = ['#F87171', '#FCD34D', '#7DD3FC', '#86EFAC'];

const SourceRefs = ({ refs, sourcesById }) => {
  const list = (refs || []).map((id) => sourcesById[id]).filter(Boolean);
  if (!list.length) return null;
  return (
    <p className="text-xs text-gray-500 mt-1.5 break-words">
      Sources:{' '}
      {list.map((s, i) => (
        <span key={s.id}>
          {i > 0 && '; '}
          <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-sky-700 underline break-words">{s.title}</a>
        </span>
      ))}
    </p>
  );
};

const NoteList = ({ items, sourcesById, testId }) => (
  <div className="space-y-4" data-testid={testId}>
    {items.map((it, i) => (
      <div key={it.label || i}>
        {it.label && <div className="text-sm font-bold text-gray-900">{it.label}</div>}
        <p className="text-sm text-gray-700 leading-relaxed break-words">{it.text}</p>
        <SourceRefs refs={it.sourceRefs} sourcesById={sourcesById} />
      </div>
    ))}
  </div>
);

export default function AIRiskCheckPreview() {
  const { roleName: encoded } = useParams();
  const roleName = decodeURIComponent(encoded || '');
  const { user } = useAuth();
  const navigate = useNavigate();
  const [role, setRole] = useState(null);
  const [meta, setMeta] = useState(null);
  const [shared, setShared] = useState(null);
  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/ai-risk-check/check`, { params: { role: roleName } });
        setRole(r.data.role);
        setMeta(r.data.meta || null);
        setShared(r.data.shared || null);
      } catch (e) {
        const code = e?.response?.status;
        if (code === 403 || code === 423) setLocked(true);
        else setErr(e?.response?.data?.detail || 'Could not load.');
      } finally { setLoading(false); }
    })();
  }, [user, roleName, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F0F9FF]">
        <Loader2 className="w-8 h-8 animate-spin text-sky-600" />
      </div>
    );
  }

  if (locked) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-indigo-50">
        <Navbar />
        <div className="pt-28 pb-16 px-4">
          <div className="max-w-xl mx-auto text-center bg-white border border-gray-200 rounded-3xl p-10 shadow-sm" data-testid="ar-preview-locked">
            <Lock size={32} className="mx-auto text-gray-400 mb-3" />
            <h1 className="text-2xl font-extrabold text-gray-900">Locked</h1>
            <p className="text-sm text-gray-600 mt-2">Select this role from Is AI Coming for Your Role? first to unlock the full check.</p>
            <Link to="/ai-risk-check">
              <Button className="mt-6 bg-sky-600 hover:bg-sky-700 text-white">Go to AI Risk Check</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!role) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-indigo-50">
        <Navbar />
        <div className="pt-28 pb-16 px-4 text-center">
          <p className="text-rose-700 font-semibold">{err || 'Role not found.'}</p>
        </div>
      </div>
    );
  }

  const registry = shared?.sectionRegistry || [];
  const sourcesById = Object.fromEntries((shared?.sources || []).map((s) => [s.id, s]));
  const research = shared?.researchCoverage || {};
  const aboutNotes = [
    ['physicalAndDemandNote', 'Physical work and demand'],
    ['decisionCheck', 'Before you decide'],
    ['methodology', 'How the cards were reviewed'],
    ['evidenceBoundary', 'What the evidence can and cannot show'],
    ['reviewPolicy', 'Review policy'],
  ].filter(([k]) => meta?.[k]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-indigo-50">
      <Navbar />
      <div className="pt-24 md:pt-28 pb-16 px-4">
        <div className="max-w-4xl mx-auto min-w-0">
          <div className="mb-6 flex items-center justify-between flex-wrap gap-2">
            <button type="button" onClick={() => navigate('/ai-risk-check')}
              className="inline-flex items-center text-sm font-semibold text-gray-600 hover:text-gray-900"
              data-testid="ar-preview-back-btn">
              <ChevronLeft size={16} className="mr-1" /> Back to my picks
            </button>
            <Link to="/dashboard" className="text-xs font-bold uppercase tracking-widest text-gray-500 hover:text-gray-700">Dashboard</Link>
          </div>

          <div className="text-xs font-bold tracking-widest text-sky-700 uppercase mb-1 inline-flex items-center gap-2" data-testid="ar-page-title">
            <Zap size={14} /> {meta?.title || 'Is AI Coming for Your Role?'}
          </div>
          {meta?.updated && (
            <div className="text-[11px] text-gray-500 mb-3" data-testid="ar-updated-date">Updated: {meta.updated}</div>
          )}

          {meta?.predictionNotice && (
            <div className="flex items-start gap-2.5 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 mb-4" data-testid="ar-prediction-notice">
              <Info size={16} className="text-amber-700 shrink-0 mt-0.5" />
              <p className="text-sm text-amber-900 leading-relaxed break-words">{meta.predictionNotice}</p>
            </div>
          )}

          {/* ── Result card ── */}
          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
            className="rounded-2xl p-5 md:p-7 shadow-xl text-white min-w-0"
            style={{ background: '#0F172A' }}
            data-testid="ar-result"
          >
            <label className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: '#94A3B8' }}>
              {meta?.subtitle || 'Your role at a glance'}
            </label>
            <div className="mb-2 mt-1">
              <h3 className="text-xl md:text-2xl font-bold leading-tight break-words" data-testid="ar-role-name">{role.name}</h3>
              <div className="text-xs text-gray-400 mt-0.5" data-testid="ar-role-department">{role.department}</div>
            </div>

            <div className="space-y-3 mt-5">
              {registry.map((s, i) => role[s.key] && (
                <div key={s.key} className="rounded-xl p-4 min-w-0" style={{ background: '#1E293B', border: '1px solid #334155' }} data-testid={`ar-point-${s.key}`}>
                  <div className="text-xs font-bold uppercase tracking-wider mb-2 break-words" style={{ color: POINT_TONES[i % POINT_TONES.length] }} data-testid={`ar-point-label-${s.key}`}>
                    {s.label}
                  </div>
                  <p className="text-sm text-gray-200 leading-relaxed break-words" data-testid={`ar-point-text-${s.key}`}>{role[s.key]}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* ── Shared notes — once per pathway ── */}
          {shared && (
            <motion.div
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.1 }}
              className="mt-6 space-y-3"
              data-testid="ar-shared-notes"
            >
              <div className="text-xs font-bold uppercase tracking-wider text-gray-500 px-1">Shared notes for this pathway</div>

              {(shared.overview?.length > 0 || aboutNotes.length > 0) && (
                <Accordion title="Overview" testId="ar-acc-overview">
                  <div className="space-y-4">
                    {shared.overview?.length > 0 && <NoteList items={shared.overview} sourcesById={sourcesById} testId="ar-overview-list" />}
                    {aboutNotes.map(([k, label]) => (
                      <div key={k} data-testid={`ar-about-${k}`}>
                        <div className="text-sm font-bold text-gray-900">{label}</div>
                        <p className="text-sm text-gray-700 leading-relaxed break-words">{meta[k]}</p>
                      </div>
                    ))}
                  </div>
                </Accordion>
              )}

              {shared.readingNotes?.length > 0 && (
                <Accordion title="Reading notes" testId="ar-acc-reading-notes">
                  <NoteList items={shared.readingNotes} sourcesById={sourcesById} testId="ar-reading-notes-list" />
                </Accordion>
              )}

              {research.modelNotes?.length > 0 && (
                <Accordion title="Model research" testId="ar-acc-model-research">
                  <div className="space-y-4">
                    {(research.reviewedOn || research.scope) && (
                      <p className="text-sm text-gray-700 leading-relaxed break-words" data-testid="ar-research-scope">
                        {research.reviewedOn && <span className="font-semibold text-gray-900">Reviewed {research.reviewedOn}. </span>}
                        {research.scope}
                      </p>
                    )}
                    <NoteList items={research.modelNotes} sourcesById={sourcesById} testId="ar-model-notes-list" />
                    {research.availabilityRule && <p className="text-sm text-gray-700 leading-relaxed break-words" data-testid="ar-availability-rule">{research.availabilityRule}</p>}
                    {research.benchmarkRule && <p className="text-sm text-gray-700 leading-relaxed break-words" data-testid="ar-benchmark-rule">{research.benchmarkRule}</p>}
                  </div>
                </Accordion>
              )}

              {research.limitations?.length > 0 && (
                <Accordion title="Limitations" testId="ar-acc-limitations">
                  <ul className="space-y-2" data-testid="ar-limitations-list">
                    {research.limitations.map((t, i) => (
                      <li key={i} className="text-sm text-gray-700 leading-relaxed flex items-start gap-2">
                        <span className="mt-2 w-1 h-1 rounded-full bg-gray-500 shrink-0" />
                        <span className="break-words min-w-0">{t}</span>
                      </li>
                    ))}
                  </ul>
                </Accordion>
              )}

              {shared.sources?.length > 0 && (
                <Accordion title={`Sources (${shared.sources.length})`} testId="ar-acc-sources">
                  <div className="space-y-3" data-testid="ar-sources-list">
                    {shared.sources.map((s) => (
                      <div key={s.id} className="rounded-xl border border-gray-200 p-3 min-w-0" data-testid={`ar-source-${s.id}`}>
                        <div className="text-sm font-semibold text-gray-900 break-words">
                          <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-sky-700 hover:text-sky-900 underline">{s.title}</a>
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5 break-words">
                          {[s.publisher, s.kind, s.publishedOn && `Published ${s.publishedOn}`, s.reviewedOn && `Reviewed ${s.reviewedOn}`].filter(Boolean).join(' · ')}
                        </div>
                        {s.finding && <p className="text-sm text-gray-700 leading-relaxed mt-1.5 break-words">{s.finding}</p>}
                      </div>
                    ))}
                  </div>
                </Accordion>
              )}
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}
